import { useEffect, useState } from "preact/hooks";
import { equal } from "../utils";
import { sha256 } from "crypto-hash";
import { baseUrl, getAccessToken, logout, tryRefreshToken } from ".";

let refreshCount = 0;

export interface GraphQlResult<T> {
    data?: T
    errors?: {
        message: string,
        extensions?: any
    }[];
    exception?: any;
};

// todo: all the booleans should be moved into an "opts"
export async function fetchGraphql<T, TVars>(name: string, query: string, variables: TVars, authenticate = true, refresh = true, useApq = true, catchExceptions = true): Promise<GraphQlResult<T>> {
    try {
        const operation = {
            "operationName": name,
            "variables": variables,
            "query": useApq ? undefined : query,
            "extensions": !useApq ? undefined : {
                "persistedQuery": {
                    "sha256Hash": await sha256(query),
                    "version": 1
                }
            }
        };

        const headers = {
            "content-type": "application/json;charset=utf-8",
            ... (!authenticate ? {} : {
                "Authorization": "Bearer " + getAccessToken()
            })
        };

        let res = await fetch(baseUrl + "/graphql", {
            method: "POST",
            headers,
            mode: "cors",
            body: JSON.stringify(operation)
        });

        let json = (await res.json()) as GraphQlResult<T>;

        // Handle AutomaticPersistedQueries
        if (useApq && isApqError(json)) {
            operation.query = query;
            res = await fetch(baseUrl + "/graphql", {
                method: "POST",
                headers,
                mode: "cors",
                body: JSON.stringify(operation)
            });
            json = (await res.json()) as GraphQlResult<T>;
        }

        // handle refreshing the token and retrying the query if we get an unauthorized response
        if (authenticate) {
            if (res.status == 401 || isUnauthorizedError(json)) {
                refreshCount++;
                if (refresh && refreshCount < 10) {
                    if (await tryRefreshToken()) {
                        return await fetchGraphql<T, TVars>(name, query, variables, true, false);
                    }
                }
                refreshCount = 0;
                logout();
            }
            else if (refreshCount > 0) refreshCount = Math.max(0, refreshCount - 1);
        }

        return json;
    }
    catch (e: unknown) {
        if (!catchExceptions) {
            throw e;
        }
        return {
            exception: e
        };
    }
}

export function useGraphql<T, TVars>(name: string, query: string, variables: TVars, opts?: { skip?: boolean }) {
    const [result, setResult] = useState<T | null>(null);
    const [previousVars, setPreviousVars] = useState<TVars>(variables);

    if (!equal(previousVars, variables)) {
        setPreviousVars(variables);
    }

    useEffect(() => {
        if(opts?.skip) return;
        fetchGraphql<T, TVars>(name, query, variables)
            .then(response => {
                setResult(response.data ?? null);
            });
    }, [name, opts?.skip, query, previousVars]);

    return { result };
}

export function isUnauthorizedError<T>(result: GraphQlResult<T>) {
    return (result.errors && result.errors.some(n => n.extensions?.code === "UNAUTHORIZED_ACCESS" || -1 !== n.message.toLowerCase().indexOf("unauthorized"))) || false;
}

export function isApqError<T>(result: GraphQlResult<T>) {
    return (result.errors && result.errors.some(n => n.extensions?.code === "PERSISTED_QUERY_NOT_FOUND" || -1 !== n.message.toLowerCase().indexOf("PersistedQueryNotFound"))) || false;
}
